import { useState } from 'react';
import { Building, Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = ['Building', 'Car', 'Pill', 'Shirt', 'Cpu', 'Wheat', 'Cog', 'Zap', 'HardHat'];

const INITIAL_FORM = {
  name: '', name_es: '', description: '', description_es: '',
  features: '', features_es: '', image_url: '', icon: 'Building',
  order: 0, is_published: true
};

const SectorManager = ({ sectors, onRefresh, isSpanish }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const resetForm = () => setForm(INITIAL_FORM);

  const openEdit = (sector) => {
    setEditing(sector);
    setForm({
      name: sector.name || '',
      name_es: sector.name_es || '',
      description: sector.description || '',
      description_es: sector.description_es || '',
      features: (sector.features || []).join('\n'),
      features_es: (sector.features_es || []).join('\n'),
      image_url: sector.image_url || '',
      icon: sector.icon || 'Building',
      order: sector.order || 0,
      is_published: sector.is_published !== false
    });
    setIsDialogOpen(true);
  };

  const openNew = () => {
    resetForm();
    setEditing(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      
      const payload = {
        ...form,
        features: form.features.split('\n').filter(f => f.trim()),
        features_es: form.features_es.split('\n').filter(f => f.trim())
      };
      
      const url = editing 
        ? `${API_URL}/api/admin/cms/sectors/${editing._id}`
        : `${API_URL}/api/admin/cms/sectors`;
      const method = editing ? 'PUT' : 'POST';
      
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload), credentials: 'include' });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Sector guardado' : 'Sector saved successfully');
        setIsDialogOpen(false);
        setEditing(null);
        resetForm();
        onRefresh();
      } else {
        toast.error(isSpanish ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const handleDelete = async (sectorId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar este sector?' : 'Delete this sector?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/sectors/${sectorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Sector eliminado' : 'Sector deleted');
        onRefresh();
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{isSpanish ? 'Sectores Industriales' : 'Industry Sectors'}</CardTitle>
          <Button onClick={openNew} className="btn-accent">
            <Plus className="w-4 h-4 mr-2" />
            {isSpanish ? 'Nuevo Sector' : 'New Sector'}
          </Button>
        </CardHeader>
        <CardContent>
          {sectors.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-12 h-12 text-steel-300 mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'No hay sectores' : 'No sectors yet'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isSpanish ? 'Nombre' : 'Name'}</TableHead>
                  <TableHead>{isSpanish ? 'Icono' : 'Icon'}</TableHead>
                  <TableHead>{isSpanish ? 'Orden' : 'Order'}</TableHead>
                  <TableHead>{isSpanish ? 'Estado' : 'Status'}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sectors.map(sector => (
                  <TableRow key={sector._id}>
                    <TableCell>
                      <span className="font-medium">{sector.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="bg-steel-100 px-2 py-1 rounded text-sm">{sector.icon}</span>
                    </TableCell>
                    <TableCell>{sector.order || 0}</TableCell>
                    <TableCell>
                      {sector.is_published ? (
                        <span className="text-green-600 flex items-center gap-1"><Eye className="w-4 h-4" /> Published</span>
                      ) : (
                        <span className="text-steel-400 flex items-center gap-1"><EyeOff className="w-4 h-4" /> Draft</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(sector)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(sector._id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Sector Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? (isSpanish ? 'Editar Sector' : 'Edit Sector') : (isSpanish ? 'Nuevo Sector' : 'New Sector')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Nombre (Inglés)' : 'Name (English)'}</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Descripción (Inglés)' : 'Description (English)'}</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} />
              </div>
              <div>
                <Label>{isSpanish ? 'Características (Inglés) - Una por línea' : 'Features (English) - One per line'}</Label>
                <Textarea value={form.features} onChange={(e) => setForm({...form, features: e.target.value})} rows={4} placeholder="Feature 1&#10;Feature 2&#10;Feature 3" />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>{isSpanish ? 'Nombre (Español)' : 'Name (Spanish)'}</Label>
                <Input value={form.name_es} onChange={(e) => setForm({...form, name_es: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Descripción (Español)' : 'Description (Spanish)'}</Label>
                <Textarea value={form.description_es} onChange={(e) => setForm({...form, description_es: e.target.value})} rows={3} />
              </div>
              <div>
                <Label>{isSpanish ? 'Características (Español) - Una por línea' : 'Features (Spanish) - One per line'}</Label>
                <Textarea value={form.features_es} onChange={(e) => setForm({...form, features_es: e.target.value})} rows={4} placeholder="Característica 1&#10;Característica 2&#10;Característica 3" />
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div>
                <Label>{isSpanish ? 'Icono' : 'Icon'}</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({...form, icon: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(icon => (
                      <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isSpanish ? 'URL de Imagen' : 'Image URL'}</Label>
                <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} />
              </div>
              <div>
                <Label>{isSpanish ? 'Orden' : 'Order'}</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({...form, is_published: e.target.checked})} className="rounded" />
                <span>{isSpanish ? 'Publicado' : 'Published'}</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" /> {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="btn-accent">
              <Save className="w-4 h-4 mr-2" /> {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SectorManager;
